import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import { BLUE, BLUE_HOVER, BORDER, GREEN, INTER, NAVY, ON_NAVY_ACCENT, ON_NAVY_TEXT, POPPINS, SLATE, SOFT_BLUE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_MUTED, SURFACE_SUBTLE } from '../teacherStyles';
import { exerciseInsights } from './ClassPageData';
import { Metric, SectionCard } from './ClassPageUi';

interface ClassOverviewPageProps {
    className: string;
    lesson: string;
    studentCount: number;
    lessonCount: number;
    onOpenLesson: () => void;
    onReviewSpeaking: () => void;
}

const currentLessonMetrics = [
    { value: '14/18', label: 'Students complete', note: '4 still working' },
    { value: '81%', label: 'Answer accuracy', note: 'Across five exercises' },
    { value: '90', label: 'Speaking attempts', note: 'Conversation and exit ticket' },
];

export default function ClassOverviewPage({ className, lesson, studentCount, lessonCount, onOpenLesson, onReviewSpeaking }: ClassOverviewPageProps) {
    return (
        <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' }, gap: 2 }}>
                <Metric value={String(studentCount)} label="Students" note="Enrolled in this class" icon={GroupsRoundedIcon} color={BLUE} background={SOFT_BLUE} />
                <Metric value={String(lessonCount)} label="Lessons" note="In this class" icon={MenuBookRoundedIcon} color={GREEN} background={SUCCESS_SOFT} />
                <Metric value="58%" label="Lesson completion" note="7 of 12 lessons complete" icon={CheckCircleRoundedIcon} color={GREEN} background={SUCCESS_SOFT} />
                <Metric value="76%" label="Answer accuracy" note="Across all exercises" icon={QueryStatsRoundedIcon} color={BLUE} background={SOFT_BLUE} />
                <Metric value="286" label="Speaking attempts" note="Across all class lessons" icon={RecordVoiceOverRoundedIcon} color={NAVY} background={SURFACE_MUTED} />
                <Button
                    onClick={onReviewSpeaking}
                    aria-label="Review 3 flagged recordings"
                    sx={{ minWidth: 0, p: 2, borderRadius: '20px', justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', color: '#fff', backgroundColor: NAVY, boxShadow: '0 4px 12px rgba(22, 58, 99, 0.08)', '&:hover': { backgroundColor: NAVY, filter: 'brightness(1.08)' } }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 21, lineHeight: 1, color: '#fff' }}>3</Typography>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: '#fff', mt: 0.65 }}>Recordings to review</Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 11, color: ON_NAVY_TEXT, mt: 0.75 }}>Open review queue →</Typography>
                    </Box>
                    <Box sx={{ width: 34, height: 34, ml: 1, flexShrink: 0, borderRadius: '11px', display: 'grid', placeItems: 'center', color: ON_NAVY_ACCENT, backgroundColor: 'rgba(255,255,255,0.1)' }}>
                        <RecordVoiceOverRoundedIcon sx={{ fontSize: 19 }} />
                    </Box>
                </Button>
            </Box>

            <SectionCard sx={{ p: 2.5, mt: 2.5 }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>Exercise insights</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mt: 2 }}>
                    {exerciseInsights.map(item => (
                        <Box key={item.label} sx={{ p: 1.75, borderRadius: '14px', backgroundColor: SURFACE_SUBTLE }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: NAVY }}>{item.label}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: item.color, mt: 0.75 }}>{item.result}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE, mt: 0.3 }}>{item.note}</Typography>
                        </Box>
                    ))}
                </Box>
            </SectionCard>

            <SectionCard sx={{ overflow: 'hidden', mt: 2.5 }}>
                <Stack direction="row" alignItems="center" sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                    <Box>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>Current lesson</Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>In progress for {className}</Typography>
                    </Box>
                    <Chip label="Published" size="small" sx={{ ml: 'auto', fontFamily: INTER, fontWeight: 700, color: SUCCESS_TEXT, backgroundColor: SUCCESS_SOFT }} />
                    <IconButton aria-label={`Open ${lesson}`} onClick={onOpenLesson} sx={{ ml: 1, color: BLUE, backgroundColor: SOFT_BLUE, '&:hover': { backgroundColor: BLUE_HOVER } }}><ChevronRightRoundedIcon /></IconButton>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.05fr) minmax(420px, .95fr)' }, gap: 3, alignItems: 'center', p: 2.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.25}>
                        <Box sx={{ width: { xs: '100%', sm: 84 }, height: 84, flexShrink: 0, borderRadius: '18px', display: 'grid', placeItems: 'center', color: BLUE, backgroundColor: SOFT_BLUE }}>
                            <MenuBookRoundedIcon sx={{ fontSize: 34 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>{lesson}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12.5, lineHeight: 1.55, color: SLATE, mt: 0.5 }}>Students practice polite requests and measure words through listening, guided speaking, and an AI restaurant conversation.</Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                {['50 min class plan', '5 exercises'].map(label => <Chip key={label} label={label} size="small" sx={{ fontFamily: INTER, fontSize: 11.5, backgroundColor: SURFACE_MUTED, color: SLATE }} />)}
                            </Stack>
                        </Box>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
                        {currentLessonMetrics.map(metric => (
                            <Box key={metric.label} sx={{ minWidth: 0, p: 1.5, borderRadius: '14px', backgroundColor: SURFACE_SUBTLE }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, lineHeight: 1, color: NAVY }}>{metric.value}</Typography>
                                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: NAVY, mt: 0.75 }}>{metric.label}</Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 11, lineHeight: 1.4, color: SLATE, mt: 0.5 }}>{metric.note}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </SectionCard>
        </>
    );
}
